// Core types for the multi-sport session planner.
//
// The engine turns per-sport hourly scores plus raw hourly conditions into a
// ranked timeline of session windows for today and tomorrow, interpreted
// through the lens of a user's stated ability and preferences. Everything here
// is pure data; the data layer (data.ts) assembles SessionData from Supabase
// and the React island renders a SessionPlan.

/** The sports the planner supports (its own grouping). */
export const PLANNER_SPORTS = [
  "surf",
  "bodyboarding",
  "wind",
  "foil",
  "sup",
  "swim",
  "diving",
  "sailing",
] as const;
export type PlannerSport = (typeof PLANNER_SPORTS)[number];

export type Ability = "beginner" | "intermediate" | "advanced" | "expert";

/** today | tomorrow */
export type DayOffset = 0 | 1;

/** Display register, derived from ability. */
export type Comprehension = "plain" | "technical";

export type TideState = "rising" | "falling" | "high" | "low";

/**
 * One hour of conditions for the active sport. The score is the unbiased
 * platform score (0-10); the raw fields drive comfort gates and explanations.
 */
export type SessionHour = {
  hour: number; // 0-23 local
  dayOffset: DayOffset;
  score: number; // 0-10 platform score for the active sport
  isDay: boolean;
  waveHeightM: number | null;
  /** Stored wind-wave component, when marine data provides it. */
  windWaveHeightM?: number | null;
  swellPeriodS: number | null;
  swellDirDeg: number | null;
  windSpeedKmh: number;
  windDirDeg: number | null;
  windGustKmh: number | null;
  airTempC: number | null;
  waterTempC: number | null; // per-day value broadcast to each hour
  tidePosition: number | null; // 0 (low) .. 1 (high)
  tideState: TideState | null;
  weatherCode: number | null;
  /** Atmospheric visibility above the surface. Never underwater visibility. */
  visibilityM: number | null;
  /** Direct Secchi depth when available. Null is never replaced by air visibility. */
  underwaterVisibilityM?: number | null;
  underwaterVisibilitySource?: "secchi" | "chlorophyll-inferred" | "unavailable";
  underwaterVisibilityProvider?: "copernicus" | "water-quality" | null;
  /** Canonical stored ocean-current magnitude converted to m/s. */
  currentVelocityMs?: number | null;
  /** Explicitly unavailable until a reviewed canonical direction carrier exists. */
  currentDirectionDeg?: null;
  /** Exact hourly canonical safety transported with this sport score. */
  safetyState?: "clear" | "flagged-red" | "flagged-amber" | "no-data";
  safetyNotes?: string[];
  /** Producer-owned evidence confidence for this exact quality score. */
  confidence?: import("../conditions/factor-explanations").FactorConfidence;
  confidenceReasons?: string[];
};

export type ComfortBand = "in-range" | "too-strong" | "too-weak";
export type FitAvailability = "available" | "unavailable";

/** Verdict for a single hour, given the user's preferences. */
export type ComfortVerdict = {
  acceptable: boolean; // passes ability + preference gates
  band: ComfortBand;
  fitMultiplier: number; // 0..1.2, preference alignment
  fitAvailability: FitAvailability;
  excludedBy: string | null; // "wave size" | "wind" | "water temp" | "timing" | "water quality"
};

export type WindowLabel =
  | "best"
  | "good"
  | "possible"
  | "not-recommended"
  | "too-strong"
  | "too-weak";

export type SessionWindow<THour extends SessionHour = SessionHour> = {
  dayOffset: DayOffset;
  dayLabel: "Today" | "Tomorrow";
  startHour: number; // inclusive
  endHour: number; // exclusive
  timeLabel: string; // "06:00 - 10:00"
  peakStartHour: number; // tightened headline window start (== startHour when <=2h)
  peakEndHour: number; // tightened headline window end (exclusive)
  peakScore: number; // mean platform score over the peak hours (what the card headlines)
  label: WindowLabel;
  avgScore: number; // 0-10
  fitScore: number; // 0-100, used for ranking
  hours: THour[];
  headline: string; // one-line metric summary at comprehension level
  explanation: string; // 1-2 sentence British-English narrative
  warnings: string[];
};

export type WaterComfort = {
  waterTempC: number | null;
  label: string; // e.g. "Cool" / "Cold"
  advice: string; // e.g. "Full wetsuit, boots optional"
  comfortable: boolean; // against the user's wetsuit / temperature preference
};

export type NearbySuggestion = {
  slug: string;
  name: string;
  distanceKm: number;
  score: number; // 0-10 best-of-today/tomorrow for the active sport
  note: string; // British-English one-liner
};

export type SessionPlan<
  TWindow extends SessionWindow = SessionWindow,
> = {
  sport: PlannerSport;
  ability: Ability;
  comprehension: Comprehension;
  windows: TWindow[]; // ranked, both days, all labels
  bestWindow: TWindow | null;
  hasGoodWindows: boolean;
  allDayVerdict: string | null;
  waterComfort: WaterComfort;
  nearbySuggestion: NearbySuggestion | null;
};

/** A user's preferences for one sport: a flat bag of field-key -> option value. */
export type SessionPreferences = {
  sport: PlannerSport;
  values: Record<string, string>; // includes "ability"
};
